import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Descriptions, Input, Layout, Modal, Progress, Select, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { EyeOutlined, ReloadOutlined, UnlockOutlined } from '@ant-design/icons';
import { doc, getDoc, collection, query, where, getCountFromServer, getDocs, limit, startAfter } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useAuth } from '../../context/DataContext';
import dayjs from 'dayjs';
import CustomSeatPicker from '../SelectSeat/CustomSeatPicker';
import apis from '../../apis';
import { useTranslation } from 'react-i18next';
import './SeatOccupancy.css';
import { buildSeatOccupancyLookup, getSeatLayoutBookingId, getSeatLayoutDetails, matchesSeatOccupancySearch } from '../../utils/seatOccupancyLookup';
import { loadAssignedGuestRegistrants } from '../../utils/guestRegistrants';

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;
const renderSeatBuyerName = seat => (
    <span className="seat-occupancy-buyer-name">
        {seat.isReserved ? seat.buyerName || 'Buyer not recorded' : '\u00a0'}
    </span>
);
const createLocalizedError = messageText => Object.assign(new Error(messageText), { localized: true });
const releaseErrorTranslationKeys = {
    INVALID_RELEASE_REASON: 'adminSeatRelease.errorInvalidReason',
    BOOKING_NOT_FOUND: 'adminSeatRelease.bookingMissing',
    PAID_BOOKING: 'adminSeatRelease.errorPaidBooking',
    BOOKING_NOT_ELIGIBLE: 'adminSeatRelease.errorNotEligible',
    BOOKING_ALREADY_RECONCILED: 'adminSeatRelease.errorAlreadyReconciled',
    NO_RESPONSE_TOO_SOON: 'adminSeatRelease.errorNoResponseTooSoon',
    PAPER_CANCELLATION_FAILED: 'adminSeatRelease.errorPaperCancellation',
    PENDING_INVOICE_CREATION: 'adminSeatRelease.pendingNoInvoice',
    MANUAL_CONFIRMATION_REQUIRED: 'adminSeatRelease.manualConfirmRequired',
    BOOKING_STATE_CHANGED: 'adminSeatRelease.errorStateChanged',
    BOOKING_INVOICE_CHANGED: 'adminSeatRelease.errorInvoiceChanged',
    INVOICE_CANCELLATION_UNCONFIRMED: 'adminSeatRelease.errorCancellationUnconfirmed',
    INVENTORY_RECONCILIATION_REQUIRED: 'adminSeatRelease.errorInventoryReconciliation',
};

// Load only the active event/session, in bounded pages, when staff request the lookup.
const readLookupPages = async (collectionRef, constraints) => {
    const records = [];
    let cursor;
    while (true) {
        const snapshot = await getDocs(query(collectionRef, ...constraints, ...(cursor ? [startAfter(cursor)] : []), limit(200)));
        records.push(...snapshot.docs.map(record => ({ ...record.data(), id: record.id })));
        if (snapshot.size < 200) return records;
        cursor = snapshot.docs[snapshot.docs.length - 1];
    }
};

const SeatOccupancy = () => {
    const { t } = useTranslation();
    const { currentEventId } = useAuth();
    const [loading, setLoading] = useState(true);
    const [occupancyData, setOccupancyData] = useState([]);
    const [lookupRows, setLookupRows] = useState([]);
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupLoaded, setLookupLoaded] = useState(false);
    const [lookupSearch, setLookupSearch] = useState('');
    const [lookupPage, setLookupPage] = useState(1);
    const lookupRequestId = useRef(0);
    
    // Layout Modal State
    const [isLayoutModalVisible, setIsLayoutModalVisible] = useState(false);
    const [layoutLoading, setLayoutLoading] = useState(false);
    const [selectedLayoutSession, setSelectedLayoutSession] = useState(null);
    const [seatLayout, setSeatLayout] = useState({});
    const [layoutSearch, setLayoutSearch] = useState('');
    const layoutRequestId = useRef(0);
    const [isReleaseModalVisible, setIsReleaseModalVisible] = useState(false);
    const [selectedLockedSeat, setSelectedLockedSeat] = useState(null);
    const [selectedLockedBooking, setSelectedLockedBooking] = useState(null);
    const [releaseBookingLoading, setReleaseBookingLoading] = useState(false);
    const [releaseReason, setReleaseReason] = useState();
    const [releaseNote, setReleaseNote] = useState('');
    const [manualProviderConfirmation, setManualProviderConfirmation] = useState(false);
    const releaseReasonOptions = [
        { value: 'customer_declined', label: t('adminSeatRelease.reasonCustomerDeclined') },
        { value: 'no_response_after_one_hour', label: t('adminSeatRelease.reasonNoResponse') },
    ];

    useEffect(() => {
        lookupRequestId.current += 1;
        layoutRequestId.current += 1;
        setIsLayoutModalVisible(false);
        setLayoutLoading(false);
        setSelectedLayoutSession(null);
        setSeatLayout({});
        setLayoutSearch('');
        setLookupRows([]);
        setLookupLoaded(false);
        setLookupLoading(false);
        setLookupSearch('');
        setLookupPage(1);
        if (currentEventId) {
            fetchData();
        }
        return () => {
            lookupRequestId.current += 1;
            layoutRequestId.current += 1;
        };
    }, [currentEventId]);

    const fetchLookup = async () => {
        const requestId = ++lookupRequestId.current;
        const eventId = currentEventId || 'APCS2026';
        setLookupLoading(true);
        setLookupLoaded(false);
        setLookupRows([]);
        try {
            const [registrations, bookings, assignmentSnapshot] = await Promise.all([
                readLookupPages(collection(db, 'Registrants2025'), [where('eventId', '==', eventId)]),
                readLookupPages(collection(db, 'publicBookings'), [where('eventId', '==', eventId)]),
                getDoc(doc(db, 'sessionAssignments', eventId)),
            ]);
            const assignments = assignmentSnapshot.data()?.assignments || {};
            const guestRegistrations = await loadAssignedGuestRegistrants(Object.values(assignments)
                .map(entries => ({ registrantIds: entries.map(entry => entry.registrantId).filter(Boolean) })), registrations);
            const seats = [];
            for (const session of occupancyData.filter(item => item.isGenerated)) {
                if (requestId !== lookupRequestId.current) return;
                seats.push(...await readLookupPages(collection(db, `seats${eventId}`), [
                    where('sessionId', '==', session.sessionId), where('venueId', '==', session.venueId),
                ]));
            }
            if (requestId !== lookupRequestId.current) return;
            setLookupRows(buildSeatOccupancyLookup({
                sessions: occupancyData, registrations: [...registrations, ...guestRegistrations], bookings, seats, assignments,
            }));
            setLookupLoaded(true);
            setLookupPage(1);
        } catch (error) {
            if (requestId !== lookupRequestId.current) return;
            console.error('Error loading registrant and reservation lookup:', error);
            message.error('Failed to load the lookup. Please try again.');
        } finally {
            if (requestId === lookupRequestId.current) setLookupLoading(false);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const docRef = doc(db, 'events', currentEventId || 'APCS2026');
            const docSnap = await getDoc(docRef);
            
            if (!docSnap.exists()) {
                message.error("Event not found");
                setLoading(false);
                return;
            }

            const data = docSnap.data();
            const seatsGenerated = data.sessionsSeatsGenerated || {};
            const venues = data.venues || [];

            const sessionsList = [];
            // Keep orchestra slots out of competition inventory if old schedules overlap.
            const orchestraSlots = new Set((data.orchestraSessions || []).map(
                os => `${os.venue}|${os.date}_${os.time}`
            ));

            // Add Performer Sessions
            venues.forEach(venue => {
                if (venue.sessions) {
                    Object.keys(venue.sessions).forEach(dateStr => {
                        const timeslots = venue.sessions[dateStr];
                        timeslots.forEach(timeStr => {
                            const sId = `${dateStr}_${timeStr}`;
                            if (orchestraSlots.has(`${venue.id}|${sId}`)) return;
                            sessionsList.push({
                                key: `${venue.id}_${sId}`,
                                type: 'Competition',
                                venue: venue.label || venue.id,
                                venueId: venue.id,
                                date: dateStr,
                                time: timeStr,
                                sessionId: sId,
                                isGenerated: !!seatsGenerated[`${venue.id}_${sId}`] || !!seatsGenerated[sId]
                            });
                        });
                    });
                }
            });

            // Fetch Seat Counts for generated sessions
            const seatsCollection = collection(db, `seats${currentEventId || 'APCS2026'}`);
            
            const results = await Promise.all(sessionsList.map(async (session) => {
                if (!session.isGenerated) {
                    return { ...session, total: 0, available: 0, locked: 0, booked: 0 };
                }
                
                try {
                    const qSession = query(seatsCollection, where('sessionId', '==', session.sessionId), where('venueId', '==', session.venueId));
                    const totalSnapshot = await getCountFromServer(qSession);
                    const total = totalSnapshot.data().count;

                    const qLocked = query(seatsCollection, where('sessionId', '==', session.sessionId), where('venueId', '==', session.venueId), where('status', '==', 'locked'));
                    const lockedSnapshot = await getCountFromServer(qLocked);
                    const locked = lockedSnapshot.data().count;

                    const qAvailable = query(seatsCollection, where('sessionId', '==', session.sessionId), where('venueId', '==', session.venueId), where('status', '==', 'available'));
                    const availableSnapshot = await getCountFromServer(qAvailable);
                    const available = availableSnapshot.data().count;

                    const qBooked = query(seatsCollection, where('sessionId', '==', session.sessionId), where('venueId', '==', session.venueId), where('status', 'in', ['booked', 'reserved']));
                    const bookedSnapshot = await getCountFromServer(qBooked);
                    const booked = bookedSnapshot.data().count;

                    return { ...session, total, available, locked, booked };
                } catch (e) {
                    console.error("Failed to fetch seat count for session", session.sessionId, e);
                    return { ...session, total: 0, available: 0, locked: 0, booked: 0 };
                }
            }));

            // Sort by Date and Time
            results.sort((a, b) => {
                if (a.date !== b.date) return a.date.localeCompare(b.date);
                return a.time.localeCompare(b.time);
            });

            setOccupancyData(results);

        } catch (error) {
            console.error("Error fetching occupancy data:", error);
            message.error("Failed to load occupancy data");
        } finally {
            setLoading(false);
        }
    };

    const handleViewLayout = async (session) => {
        const requestId = ++layoutRequestId.current;
        const eventId = currentEventId || 'APCS2026';
        setSelectedLayoutSession(session);
        setIsLayoutModalVisible(true);
        setLayoutLoading(true);
        setSeatLayout({});
        setLayoutSearch('');

        try {
            const seatsCollection = collection(db, `seats${eventId}`);
            const q = query(seatsCollection, where('sessionId', '==', session.sessionId), where('venueId', '==', session.venueId));
            const snapshot = await getDocs(q);
            
            const fetchedSeats = [];
            snapshot.forEach(docSnap => {
                const data = docSnap.data();
                fetchedSeats.push({ ...data, id: docSnap.id });
            });
            if (requestId !== layoutRequestId.current) return;

            const bookingIds = [...new Set(fetchedSeats.map(getSeatLayoutBookingId).filter(Boolean))];
            const bookingResults = await Promise.allSettled(bookingIds.map(bookingId =>
                getDoc(doc(db, 'publicBookings', bookingId))));
            if (requestId !== layoutRequestId.current) return;
            const bookingsById = new Map();
            bookingResults.forEach((result, index) => {
                if (result.status === 'fulfilled' && result.value.exists()) {
                    bookingsById.set(bookingIds[index], { ...result.value.data(), id: bookingIds[index] });
                }
            });
            if (bookingResults.some(result => result.status === 'rejected')) {
                message.warning('Some booking details could not be loaded. Names may be incomplete; reopen the layout to retry.');
            }

            // Format layout
            const groupedByArea = fetchedSeats.reduce((acc, seat) => {
                const area = seat.areaType;
                if (!acc[area]) acc[area] = [];
                acc[area].push(seat);
                return acc;
            }, {});

            const finalLayouts = {};
            const areaOrder = ['presto', 'allegro', 'lento'];
            const rightSideMax = {
                A: 9, B: 10, C: 10, D: 9, E: 9, F: 11,
                G: 9, H: 9, I: 9, J: 8, K: 8, L: 9, M: 10,
                N: 13, O: 13, P: 13, Q: 13, R: 12, S: 12, T: 12, U: 12
            };

            const areasInDb = Object.keys(groupedByArea);
            const sortedAreas = areasInDb.sort((a, b) => {
                const idxA = areaOrder.indexOf(a.toLowerCase());
                const idxB = areaOrder.indexOf(b.toLowerCase());
                if (idxA !== -1 && idxB !== -1) return idxA - idxB;
                if (idxA !== -1) return -1;
                if (idxB !== -1) return 1;
                return a.localeCompare(b);
            });

            sortedAreas.forEach(area => {
                if (groupedByArea[area]) {
                    const rowsObject = groupedByArea[area].reduce((acc, seat) => {
                        const row = seat.row;
                        if (!acc[row]) acc[row] = [];
                        acc[row].push(seat);
                        return acc;
                    }, {});

                    const sortedRowKeys = Object.keys(rowsObject).sort((a, b) => a.localeCompare(b));

                    finalLayouts[area] = sortedRowKeys.map(rowKey => {
                        const rowSeats = rowsObject[rowKey];
                        rowSeats.sort((a, b) => b.number - a.number);
                        
                        const mappedRow = rowSeats.map(seat => {
                            const details = getSeatLayoutDetails(seat, bookingsById.get(getSeatLayoutBookingId(seat)), {
                                eventId, venueId: session.venueId, sessionId: session.sessionId,
                            });
                            
                            return {
                                ...details,
                                id: seat.id,
                                number: seat.number,
                                row: seat.row,
                                isReserved: seat.status !== 'available',
                                status: seat.status,
                                seatLabel: seat.seatLabel,
                                lockedByBookingId: seat.lockedByBookingId,
                                tooltip: (
                                    <div style={{ textAlign: 'center' }}>
                                        <strong style={{ display: 'block', marginBottom: '4px' }}>Seat {seat.seatLabel}</strong>
                                        <div>Status: {String(seat.status || 'unknown').toUpperCase()}</div>
                                        {seat.status !== 'available' && <div>Buyer: {details.buyerName || 'Not recorded'}</div>}
                                        {details.registrantName && <div>Registrant: {details.registrantName}</div>}
                                        {details.userEmail && <div>Email: {details.userEmail}</div>}
                                        {details.bookingId && <div>Booking ID: {details.bookingId}</div>}
                                    </div>
                                ),
                                statusColor: seat.status === 'booked' ? '#ff4d4f' : seat.status === 'reserved' ? '#bfbfbf' : seat.status === 'locked' ? '#faad14' : undefined
                            };
                        });

                        if (session.venueId === 'Venue1') {
                            const splitSeatNumber = rightSideMax[rowKey];
                            if (splitSeatNumber) {
                                const splitIndex = mappedRow.findIndex(s => s.number === splitSeatNumber);
                                if (splitIndex !== -1) {
                                    mappedRow.splice(splitIndex, 0, null, null);
                                }
                            }
                        }

                        return mappedRow;
                    });
                }
            });

            setSeatLayout(finalLayouts);

        } catch (error) {
            if (requestId !== layoutRequestId.current) return;
            console.error("Error loading layout:", error);
            message.error("Failed to load layout details");
        } finally {
            if (requestId === layoutRequestId.current) setLayoutLoading(false);
        }
    };

    const closeLayoutModal = () => {
        layoutRequestId.current += 1;
        setIsLayoutModalVisible(false);
        setLayoutLoading(false);
        setSeatLayout({});
        setSelectedLayoutSession(null);
        setLayoutSearch('');
    };

    const closeReleaseModal = (force = false) => {
        if (releaseBookingLoading && !force) return;
        setIsReleaseModalVisible(false);
        setSelectedLockedSeat(null);
        setSelectedLockedBooking(null);
        setReleaseReason();
        setReleaseNote('');
        setManualProviderConfirmation(false);
    };

    const handleLockedSeatClick = async (seat) => {
        if (seat.status !== 'locked') return;
        if (!seat.lockedByBookingId) {
            message.warning(t('adminSeatRelease.noLinkedBooking'));
            return;
        }

        setSelectedLockedSeat(seat);
        setSelectedLockedBooking(null);
        setReleaseReason();
        setReleaseNote('');
        setManualProviderConfirmation(false);
        setIsReleaseModalVisible(true);
        setReleaseBookingLoading(true);
        try {
            const bookingSnapshot = await getDoc(doc(db, 'publicBookings', seat.lockedByBookingId));
            if (!bookingSnapshot.exists()) {
                throw createLocalizedError(t('adminSeatRelease.bookingMissing'));
            }
            setSelectedLockedBooking({ id: bookingSnapshot.id, ...bookingSnapshot.data() });
        } catch (error) {
            console.error('Error loading locked booking:', error);
            message.error(error.localized ? error.message : t('adminSeatRelease.loadFailed'));
            setIsReleaseModalVisible(false);
        } finally {
            setReleaseBookingLoading(false);
        }
    };

    const handleReleaseBooking = async () => {
        if (!selectedLockedBooking || !releaseReason) return;
        const bookingStatus = String(selectedLockedBooking.paymentStatus || '').toLowerCase();
        if (bookingStatus === 'pending' && !selectedLockedBooking.invoiceId) {
            message.error(t('adminSeatRelease.pendingNoInvoice'));
            return;
        }
        const requiresManualConfirmation = bookingStatus === 'failed' && !selectedLockedBooking.invoiceId;
        if (requiresManualConfirmation && !manualProviderConfirmation) {
            message.error(t('adminSeatRelease.manualConfirmRequired'));
            return;
        }

        setReleaseBookingLoading(true);
        try {
            const authToken = await auth.currentUser?.getIdToken();
            if (!authToken) throw createLocalizedError(t('adminSeatRelease.sessionExpired'));

            const response = await apis.publicTicket.releaseBooking({
                bookingId: selectedLockedBooking.id,
                reason: releaseReason,
                note: releaseNote,
                manualProviderConfirmation,
            }, authToken);
            if (!response?.data?.released) {
                const translationKey = releaseErrorTranslationKeys[response?.error?.data?.code]
                    || 'adminSeatRelease.releaseFailed';
                throw createLocalizedError(t(translationKey));
            }

            message.success(t('adminSeatRelease.releaseSuccess'));
            closeReleaseModal(true);
            if (selectedLayoutSession) await handleViewLayout(selectedLayoutSession);
            await fetchData();
        } catch (error) {
            console.error('Error releasing booking:', error);
            message.error(error.localized ? error.message : t('adminSeatRelease.releaseFailed'));
        } finally {
            setReleaseBookingLoading(false);
        }
    };

    const columns = [
        {
            title: 'Session Type',
            dataIndex: 'type',
            key: 'type',
            render: text => <Tag color={text === 'Orchestra' ? 'purple' : 'blue'}>{text}</Tag>,
        },
        {
            title: 'Venue',
            dataIndex: 'venue',
            key: 'venue',
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            render: text => dayjs(text).format('DD MMM YYYY')
        },
        {
            title: 'Time',
            dataIndex: 'time',
            key: 'time',
        },
        {
            title: 'Generated',
            dataIndex: 'isGenerated',
            key: 'isGenerated',
            render: isGen => isGen ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>
        },
        {
            title: 'Total Seats',
            dataIndex: 'total',
            key: 'total',
        },
        {
            title: 'Available',
            dataIndex: 'available',
            key: 'available',
            render: val => <Text type="success" strong>{val}</Text>
        },
        {
            title: 'Locked',
            dataIndex: 'locked',
            key: 'locked',
            render: val => <Text type="warning" strong>{val}</Text>
        },
        {
            title: 'Booked',
            dataIndex: 'booked',
            key: 'booked',
            render: val => <Text type="danger" strong>{val}</Text>
        },
        {
            title: 'Occupancy Rate',
            key: 'occupancy',
            render: (_, record) => {
                if (!record.total) return <Progress percent={0} size="small" />;
                const percent = Math.round((record.booked / record.total) * 100);
                return <Progress percent={percent} size="small" status={percent === 100 ? "success" : "active"} />;
            }
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button 
                    type="primary" 
                    size="small" 
                    icon={<EyeOutlined />} 
                    disabled={!record.isGenerated}
                    onClick={() => handleViewLayout(record)}
                >
                    View Layout
                </Button>
            )
        }
    ];

    const releaseIsPendingWithoutInvoice = Boolean(
        selectedLockedBooking
        && String(selectedLockedBooking.paymentStatus || '').toLowerCase() === 'pending'
        && !selectedLockedBooking.invoiceId
    );
    const releaseRequiresManualConfirmation = Boolean(
        selectedLockedBooking
        && String(selectedLockedBooking.paymentStatus || '').toLowerCase() === 'failed'
        && !selectedLockedBooking.invoiceId
    );
    const releaseActionDisabled = !selectedLockedBooking
        || !releaseReason
        || releaseIsPendingWithoutInvoice
        || (releaseRequiresManualConfirmation && !manualProviderConfirmation);

    const visibleLookupRows = lookupRows.filter(row => matchesSeatOccupancySearch(row, lookupSearch));
    const hasLayoutSearch = Boolean(layoutSearch.trim());
    const matchingLayoutSeats = hasLayoutSearch
        ? Object.values(seatLayout).flat(2).filter(seat => seat?.isReserved && matchesSeatOccupancySearch(seat, layoutSearch))
        : [];
    const matchingLayoutSeatIds = new Set(matchingLayoutSeats.map(seat => seat.id));
    const lookupColumns = [
        { title: 'Record Type', dataIndex: 'personType', key: 'personType' },
        { title: 'Registrant / Performer', dataIndex: 'name', key: 'name' },
        { title: 'Reservation Name', dataIndex: 'reservationName', key: 'reservationName' },
        { title: 'Venue', dataIndex: 'venue', key: 'venue' },
        { title: 'Date', dataIndex: 'date', key: 'date', render: value => dayjs(value).format('DD MMM YYYY') },
        { title: 'Time', dataIndex: 'time', key: 'time' },
        { title: 'Seats', dataIndex: 'seats', key: 'seats' },
        { title: 'Status', dataIndex: 'status', key: 'status', render: value => <Tag>{value}</Tag> },
        { title: 'Registration / Booking ID', dataIndex: 'reference', key: 'reference' },
    ];

    return (
        <Content style={{ margin: '24px 16px', padding: 24, background: '#fff', borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                    <Title level={3} style={{ margin: 0 }}>Seat Occupancy Overview</Title>
                    <Text type="secondary">
                        View numbered competition seats. Manage free-seating orchestra headcounts in Orchestra Settings and Orchestra Assignments.
                    </Text>
                </div>
                <Button 
                    type="primary" 
                    icon={<ReloadOutlined />} 
                    onClick={fetchData} 
                    loading={loading}
                >
                    Refresh Data
                </Button>
            </div>

            <Table 
                columns={columns} 
                dataSource={occupancyData} 
                loading={loading}
                pagination={{ pageSize: 20 }}
                bordered
            />

            <div style={{ marginTop: 32 }}>
                <Title level={4}>Registrant and Reservation Lookup</Title>
                <Text type="secondary">
                    Find a competition session and numbered seats by performer or reservation name. Performance assignments do not confirm ticket payment.
                </Text>
                <Space wrap style={{ display: 'flex', margin: '16px 0' }}>
                    <Input
                        aria-label="Search registrant or reservation name"
                        placeholder="Search registrant, performer, reservation name or ID"
                        allowClear
                        value={lookupSearch}
                        onChange={event => { setLookupSearch(event.target.value); setLookupPage(1); }}
                        style={{ width: 420, maxWidth: '100%' }}
                    />
                    <Button icon={<ReloadOutlined />} onClick={fetchLookup} loading={lookupLoading} disabled={loading || !currentEventId}>
                        {lookupLoaded ? 'Refresh Lookup' : 'Load Lookup'}
                    </Button>
                </Space>
                <Table
                    columns={lookupColumns}
                    dataSource={visibleLookupRows}
                    loading={lookupLoading}
                    pagination={{ current: lookupPage, pageSize: 10, onChange: setLookupPage, showSizeChanger: false }}
                    locale={{ emptyText: lookupLoaded ? 'No matching registrants or reservations.' : 'Click Load Lookup to search the current event.' }}
                    scroll={{ x: 1400 }}
                    bordered
                />
            </div>

            <Modal
                title={`Seat Layout: ${selectedLayoutSession?.venue} - ${selectedLayoutSession?.date ? dayjs(selectedLayoutSession.date).format('DD MMM YYYY') : ''} ${selectedLayoutSession?.time}`}
                open={isLayoutModalVisible}
                onCancel={closeLayoutModal}
                footer={null}
                width={1200}
                destroyOnClose
            >
                {layoutLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16 }}>Loading seat layout...</div>
                    </div>
                ) : (
                    <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '10px 0' }}>
                        <div className="seat-occupancy-layout-search">
                            <Input
                                aria-label="Search layout by registrant or buyer name"
                                placeholder="Search registrant or buyer name in this session"
                                allowClear
                                value={layoutSearch}
                                onChange={event => setLayoutSearch(event.target.value)}
                            />
                            <Text type="secondary">
                                {hasLayoutSearch
                                    ? `${matchingLayoutSeats.length} matching ${matchingLayoutSeats.length === 1 ? 'seat' : 'seats'}. Matches are outlined in gold.`
                                    : 'Buyer names appear below occupied seats. Search highlights their seats without changing the layout.'}
                            </Text>
                        </div>
                        {hasLayoutSearch && (
                            <Table
                                size="small"
                                rowKey="id"
                                pagination={false}
                                scroll={{ y: 180, x: 700 }}
                                dataSource={matchingLayoutSeats}
                                columns={[
                                    { title: 'Seat', dataIndex: 'seatLabel', key: 'seatLabel' },
                                    { title: 'Buyer', dataIndex: 'buyerName', key: 'buyerName', render: value => value || 'Not recorded' },
                                    { title: 'Registrant', dataIndex: 'registrantName', key: 'registrantName', render: value => value || '-' },
                                    { title: 'Status', dataIndex: 'status', key: 'status', render: value => String(value).toUpperCase() },
                                ]}
                                locale={{ emptyText: 'No occupied seats match this name in this session.' }}
                                style={{ marginBottom: 16 }}
                            />
                        )}
                        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'center' }}>
                            <Tag color="default">Available</Tag>
                            <Tag color="orange">Locked (In Checkout)</Tag>
                            <Tag color="red">Booked</Tag>
                            <Tag color="default" style={{ background: '#bfbfbf' }}>Reserved</Tag>
                        </Space>
                        {Object.keys(seatLayout).map(areaType => {
                            const layoutForPicker = (seatLayout[areaType] || []).map(row => row.map(seat => seat ? {
                                ...seat,
                                isHighlighted: hasLayoutSearch && matchingLayoutSeatIds.has(seat.id),
                                isDimmed: hasLayoutSearch && !matchingLayoutSeatIds.has(seat.id),
                            } : null));
                            if (layoutForPicker.length === 0) return null;
                            return (
                                <div key={areaType} style={{ marginBottom: '40px' }}>
                                    <Title level={4} style={{ textAlign: 'center', textTransform: 'capitalize' }}>
                                        Tier: {areaType}
                                    </Title>
                                    <div className="seat-occupancy-layout-scroll">
                                        <CustomSeatPicker
                                            layout={layoutForPicker}
                                            selectedSeats={[]}
                                            onSelect={() => {}}
                                            onDeselect={() => {}}
                                            maxSeats={0}
                                            isReadOnly={true}
                                            onSeatClick={handleLockedSeatClick}
                                            allowLockedSeatClick={true}
                                            renderSeatCaption={renderSeatBuyerName}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Modal>

            <Modal
                className="seat-release-modal"
                title={t('adminSeatRelease.modalTitle')}
                open={isReleaseModalVisible}
                onCancel={() => closeReleaseModal()}
                onOk={handleReleaseBooking}
                okText={t('adminSeatRelease.cancelRelease')}
                cancelText={t('adminSeatRelease.keepLocked')}
                okButtonProps={{ danger: true, disabled: releaseActionDisabled, icon: <UnlockOutlined /> }}
                confirmLoading={releaseBookingLoading}
                destroyOnClose
                width={720}
            >
                {releaseBookingLoading && !selectedLockedBooking ? (
                    <div style={{ textAlign: 'center', padding: 32 }}>
                        <Spin />
                        <div style={{ marginTop: 12 }}>{t('adminSeatRelease.loadingBooking')}</div>
                    </div>
                ) : selectedLockedBooking && (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <Alert
                            type="warning"
                            showIcon
                            message={t('adminSeatRelease.warningTitle')}
                            description={t('adminSeatRelease.warningDescription')}
                        />

                        <Descriptions bordered size="small" column={2}>
                            <Descriptions.Item label={t('adminSeatRelease.seat')}>
                                {selectedLockedSeat?.seatLabel || `${selectedLockedSeat?.row || ''}${selectedLockedSeat?.number || ''}`}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('adminSeatRelease.bookingId')}>{selectedLockedBooking.id}</Descriptions.Item>
                            <Descriptions.Item label={t('adminSeatRelease.customer')}>
                                {selectedLockedBooking.userName || selectedLockedBooking.buyerName || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('adminSeatRelease.paymentStatus')}>
                                <Tag color={selectedLockedBooking.paymentStatus === 'failed' ? 'red' : 'orange'}>
                                    {String(selectedLockedBooking.paymentStatus || 'unknown').toUpperCase()}
                                </Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('adminSeatRelease.email')}>{selectedLockedBooking.userEmail || '-'}</Descriptions.Item>
                            <Descriptions.Item label={t('adminSeatRelease.paperInvoice')}>
                                {selectedLockedBooking.invoiceId || t('adminSeatRelease.unknown')}
                            </Descriptions.Item>
                        </Descriptions>

                        <div>
                            <Text strong>{t('adminSeatRelease.reason')}</Text>
                            <Select
                                style={{ width: '100%', marginTop: 6 }}
                                placeholder={t('adminSeatRelease.selectReason')}
                                options={releaseReasonOptions}
                                value={releaseReason}
                                onChange={setReleaseReason}
                                getPopupContainer={triggerNode => triggerNode.parentElement}
                            />
                        </div>

                        <div>
                            <Text strong>{t('adminSeatRelease.adminNoteOptional')}</Text>
                            <TextArea
                                style={{ marginTop: 6 }}
                                rows={3}
                                maxLength={500}
                                showCount
                                value={releaseNote}
                                onChange={event => setReleaseNote(event.target.value)}
                                placeholder={t('adminSeatRelease.notePlaceholder')}
                            />
                        </div>

                        {selectedLockedBooking.invoiceId ? (
                            <Alert
                                type="info"
                                showIcon
                                message={t('adminSeatRelease.cancelKnownInvoice')}
                            />
                        ) : releaseIsPendingWithoutInvoice ? (
                            <Alert
                                type="error"
                                showIcon
                                message={selectedLockedBooking.paymentMode === 'manual'
                                    ? 'For an unpaid manual-payment booking, verify payment and use Cancel Unpaid in Public Customers.'
                                    : t('adminSeatRelease.pendingNoInvoice')}
                            />
                        ) : (
                            <Checkbox
                                checked={manualProviderConfirmation}
                                onChange={event => setManualProviderConfirmation(event.target.checked)}
                            >
                                {t('adminSeatRelease.confirmNoInvoice')}
                            </Checkbox>
                        )}
                    </Space>
                )}
            </Modal>
        </Content>
    );
};

export default SeatOccupancy;
